/**
 * PanelGroup — wrap react-resizable-panels pour le layout 3 panneaux.
 *
 * @remarks
 * Utilisé uniquement par `DesktopShell` (≥1280px). Tablet et Mobile ont des
 * layouts différents (drawer ou panel unique).
 *
 * Lit le contexte courant + appCtx pour récupérer les tabs/centerView
 * agrégés depuis le registry, et délègue le rendu aux 3 `Panel`.
 */

import {
  Panel as RPanel,
  Group as RPanelGroup,
  Separator as PanelResizeHandle,
} from 'react-resizable-panels';
import { Suspense, useMemo } from 'react';
import { DragGhost, Panel } from '@/app/panels';
import { featureRegistry, type AppContext, type Context, type TabConfig } from '@/app/registry';
import { useLayoutStore } from '@/app/stores/use-layout-store';
import { BreadcrumbBar } from './BreadcrumbBar';
import { ActiveGlobalLoader } from './global-loader-variants';
import { GlobalLoaderZone } from './GlobalLoaderZone';
import { LoadingState } from '@/shared/components/screen-state';

interface Props {
  context: Context;
  appCtx: AppContext;
}

/** Trie une liste de tabs selon un ordre user (préserve les nouveaux tabs en queue). */
function applyTabOrder(tabs: TabConfig[], orderIds: readonly string[]): TabConfig[] {
  if (orderIds.length === 0) return tabs;
  const orderMap = new Map(orderIds.map((id, i) => [id, i]));
  return [...tabs].sort((a, b) => {
    const oa = orderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const ob = orderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return oa - ob;
  });
}

export function PanelGroup({ context, appCtx }: Props) {
  const leftDrawerOpen = useLayoutStore((s) => s.leftDrawerOpen);
  const rightDrawerOpen = useLayoutStore((s) => s.rightDrawerOpen);
  const leftActiveTab = useLayoutStore((s) => s.leftActiveTab);
  const rightActiveTab = useLayoutStore((s) => s.rightActiveTab);
  const setLeftActiveTab = useLayoutStore((s) => s.setLeftActiveTab);
  const setRightActiveTab = useLayoutStore((s) => s.setRightActiveTab);
  const tabPanelAssignments = useLayoutStore((s) => s.tabPanelAssignments);
  const tabOrder = useLayoutStore((s) => s.tabOrder);

  const leftRawTabs = featureRegistry.getLeftPanelTabs(context, appCtx);
  const rightRawTabs = featureRegistry.getRightPanelTabs(context, appCtx);
  const CenterView = featureRegistry.getCenterView(context, appCtx);

  // Applique tabPanelAssignments (tabs déplacés d'un panneau à l'autre par drag)
  // puis tabOrder (réordonnancement par drag).
  const { leftTabs, rightTabs } = useMemo(() => {
    const leftRawIds = new Set(leftRawTabs.map((t) => t.id));
    const allTabs = [...leftRawTabs, ...rightRawTabs];
    const left: TabConfig[] = [];
    const right: TabConfig[] = [];
    for (const t of allTabs) {
      const assigned = tabPanelAssignments[t.id];
      const target = assigned ?? (leftRawIds.has(t.id) ? 'left' : 'right');
      if (target === 'left') left.push(t);
      else right.push(t);
    }
    return {
      leftTabs: applyTabOrder(left, tabOrder.left),
      rightTabs: applyTabOrder(right, tabOrder.right),
    };
  }, [leftRawTabs, rightRawTabs, tabPanelAssignments, tabOrder.left, tabOrder.right]);

  const showLeft = leftDrawerOpen && leftTabs.length > 0;
  const showRight = rightDrawerOpen && rightTabs.length > 0;

  // Tous les tabs visibles (left + right) — utilisé par DragGhost pour résoudre
  // l'icône du tab draggué peu importe sa source.
  const allTabs = useMemo(() => [...leftTabs, ...rightTabs], [leftTabs, rightTabs]);

  return (
    <>
      <RPanelGroup orientation="horizontal" className="size-full">
        {showLeft && (
          <>
            <RPanel defaultSize="20%" minSize="10%" maxSize="40%">
              <Panel
                tabs={leftTabs}
                activeTabId={leftActiveTab}
                onTabClick={setLeftActiveTab}
                side="left"
              />
            </RPanel>
            <PanelResizeHandle className="bg-border hover:bg-primary-500 w-px cursor-col-resize transition-colors" />
          </>
        )}

        <RPanel
          defaultSize={showLeft && showRight ? '60%' : showLeft || showRight ? '80%' : '100%'}
          minSize="20%"
        >
          {/* Wrapper `relative` : délimite le voile de chargement global au
              panneau central (les panneaux latéraux restent utilisables). */}
          <GlobalLoaderZone>
            <div className="relative size-full">
              <Panel side="center" header={<BreadcrumbBar />}>
                {CenterView ? (
                  <Suspense fallback={<CenterLoading />}>
                    <CenterView />
                  </Suspense>
                ) : (
                  <CenterPlaceholder context={context} />
                )}
              </Panel>
              <ActiveGlobalLoader />
            </div>
          </GlobalLoaderZone>
        </RPanel>

        {showRight && (
          <>
            <PanelResizeHandle className="bg-border hover:bg-primary-500 w-px cursor-col-resize transition-colors" />
            <RPanel defaultSize="20%" minSize="10%" maxSize="40%">
              <Panel
                tabs={rightTabs}
                activeTabId={rightActiveTab}
                onTabClick={setRightActiveTab}
                side="right"
              />
            </RPanel>
          </>
        )}
      </RPanelGroup>

      {/* Ghost icon qui suit le curseur pendant un drag (portal vers body). */}
      <DragGhost allTabs={allTabs} />
    </>
  );
}

function CenterLoading() {
  return <LoadingState label="Chargement de la vue…" />;
}

function CenterPlaceholder({ context }: { context: Context }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="text-fg text-sm font-medium">Pas encore de feature pour ce contexte</p>
      <code className="bg-bg-muted text-fg-muted rounded-sm px-2 py-1 text-xs">{context}</code>
      <p className="text-fg-muted max-w-md text-xs">
        Ajoute un manifest qui déclare un <code>centerView</code> pour ce contexte.
      </p>
    </div>
  );
}
